/**
 * Tool Configuration section for the Test Suite Configuration page.
 *
 * Displays one subsection per tool extracted from the schema. Each
 * subsection contains editable fields for schema path, temporal parameter
 * name (auto-detected via `detectTemporalParameter`), evaluation criteria,
 * and allowed additional tools (as a token group with text input).
 *
 * @see Requirements 6.1–6.4
 */

import React, { useMemo, useState } from 'react';
import {
  ExpandableSection,
  SpaceBetween,
  FormField,
  Input,
  Header,
  ColumnLayout,
  Container,
  TokenGroup,
  Box,
} from '@amzn/awsui-components-react';
import { ConfigPageAction, ConfigPageState, ValidationErrors } from './types';

// ─── Constants ─────────────────────────────────────────────────────────

const DEFAULT_EVALUATION_CRITERIA = 'PNL_DEFAULT';

// ─── Props ─────────────────────────────────────────────────────────────

export interface ToolConfigSectionProps {
  state: ConfigPageState;
  dispatch: React.Dispatch<ConfigPageAction>;
  validationErrors: ValidationErrors;
}

// ─── Sub-component: Additional Tools Input with TokenGroup ─────────────

interface AdditionalToolsInputProps {
  toolName: string;
  tools: string[];
  onUpdate: (updated: string[]) => void;
}

const AdditionalToolsInput: React.FC<AdditionalToolsInputProps> = ({
  toolName,
  tools,
  onUpdate,
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (key: string) => {
    if (key === 'Enter' && inputValue.trim()) {
      const trimmed = inputValue.trim();
      if (!tools.includes(trimmed)) {
        onUpdate([...tools, trimmed]);
      }
      setInputValue('');
    }
  };

  const handleDismiss = (itemIndex: number) => {
    onUpdate(tools.filter((_, i) => i !== itemIndex));
  };

  return (
    <SpaceBetween size="xs">
      <Input
        value={inputValue}
        onChange={({ detail }) => setInputValue(detail.value)}
        onKeyDown={({ detail }) => handleKeyDown(detail.key)}
        placeholder="Type a tool name and press Enter"
        ariaLabel={`Add additional tool for ${toolName}`}
      />
      {tools.length > 0 && (
        <TokenGroup
          items={tools.map((tool) => ({
            label: tool,
            dismissLabel: `Remove ${tool}`,
          }))}
          onDismiss={({ detail }) => handleDismiss(detail.itemIndex)}
        />
      )}
    </SpaceBetween>
  );
};

// ─── Component ─────────────────────────────────────────────────────────

const ToolConfigSection: React.FC<ToolConfigSectionProps> = ({
  state,
  dispatch,
  validationErrors,
}) => {
  const toolEntries = useMemo(
    () => Object.entries(state.tools),
    [state.tools],
  );

  const handleFieldChange = (path: string, value: unknown) => {
    dispatch({ type: 'UPDATE_FIELD', path, value });
  };

  if (toolEntries.length === 0) {
    return (
      <ExpandableSection
        headerText="Tool Configuration"
        variant="container"
        defaultExpanded
      >
        <Box color="text-status-inactive" padding={{ vertical: 's' }}>
          No tools configured. Tool configuration will appear after pre-population.
        </Box>
      </ExpandableSection>
    );
  }

  return (
    <ExpandableSection
      headerText="Tool Configuration"
      variant="container"
      defaultExpanded
    >
      <SpaceBetween size="l">
        {toolEntries.map(([toolName, toolConfig]) => (
          <Container
            key={toolName}
            header={<Header variant="h3">{toolName}</Header>}
          >
            <ColumnLayout columns={2}>
              <FormField
                label="Schema path"
                description="Path to the tool's schema file"
              >
                <Input
                  value={toolConfig.schema}
                  onChange={({ detail }) =>
                    handleFieldChange(`tools.${toolName}.schema`, detail.value)
                  }
                  placeholder="schema.yaml"
                  ariaLabel={`${toolName} schema path`}
                />
              </FormField>

              <FormField
                label="Temporal parameter name"
                description="Date parameter used for time-based filtering (auto-detected from schema)"
              >
                <Input
                  value={toolConfig.temporal_parameter_name}
                  onChange={({ detail }) =>
                    handleFieldChange(
                      `tools.${toolName}.temporal_parameter_name`,
                      detail.value,
                    )
                  }
                  placeholder="e.g. month_begin_date"
                  ariaLabel={`${toolName} temporal parameter`}
                />
              </FormField>

              <FormField
                label="Evaluation criteria"
                description="Criteria used to evaluate generated test cases"
              >
                <Input
                  value={toolConfig.evaluation_criteria}
                  onChange={({ detail }) =>
                    handleFieldChange(
                      `tools.${toolName}.evaluation_criteria`,
                      detail.value,
                    )
                  }
                  placeholder={DEFAULT_EVALUATION_CRITERIA}
                  ariaLabel={`${toolName} evaluation criteria`}
                />
              </FormField>

              <FormField
                label="Allowed additional tools"
                description="Other tools that can be used alongside this tool during evaluation"
              >
                <AdditionalToolsInput
                  toolName={toolName}
                  tools={toolConfig.allowed_additional_tools}
                  onUpdate={(updated) =>
                    handleFieldChange(
                      `tools.${toolName}.allowed_additional_tools`,
                      updated,
                    )
                  }
                />
              </FormField>
            </ColumnLayout>
          </Container>
        ))}
      </SpaceBetween>
    </ExpandableSection>
  );
};

export default ToolConfigSection;
